import { Appointment, AppointmentSlot, Attachment, Patient, Message, Response, SignedUrl, PatientChartAttribute, LoginAuth, RodAuth, RefreshAuth, MfaAuth, WebauthnAuth, OtpConfig, WebAuthnConfig, WebauthnRemove, Brand, SignatureDocument, Signature, LoginHintResolution } from "./aboboApiTypes"
import { baseJsonFetcher, baseJsonFetcherWithStatus, fetcher } from "./helpers"

export const createSignedUrl = (extension: string) => {
  return fetcher<SignedUrl>(`/attachments/new`, {
    method: 'POST',
    body: {attachment: {extension}}
  })
}

export const createAttachment = (attachment) => {
  return fetcher<Attachment>(`/attachments`, {
    method: 'POST',
    body: {attachment}
  })
}

export const renewResponseToken = (responseId, headers={}) => {
  return fetcher(`/responses${responseId ? `/${responseId}/` : '/'}renew_token`,
    {
      method: 'POST',
      headers
    }
  )
}

export const updateResponse = (responseId, response, headers={}) => {
  return fetcher<Response>(`/responses/${responseId}`, {
    method: 'PATCH',
    body: {response},
    headers
  })
}

export const createResponse = (response) => {
  return fetcher<Response>(`/responses`, {
    method: 'POST',
    body: {response}
  })
}

export const createResponseAttachment = (responseId, attachment) => {
  return fetcher<Attachment>(`/responses/${responseId}/attachments`, {
    method: 'POST',
    body: {attachment}
  })
}

export const upsertPatientChartAttribute = (responseId, patientChartAttribute, headers = {}) => {
  return fetcher<PatientChartAttribute>(`/responses/${responseId}/patient_chart_attributes`, {
    method: 'POST',
    body: { patientChartAttribute },
    headers
  })
}

export const availableSlots = (date, serviceId, language) => {
  return fetcher<AppointmentSlot[]>('/availabilities', { 
    method: 'POST', 
    body: { date, serviceId, language }
  })
}

export const createAppointment = (appointment) => {
  return fetcher<Appointment>('/patients', { 
    method: 'POST',
    body: {patient: appointment}
  })
}

export const createPatientAppointment = (patientId: string, appointment, token: string) => {
  return fetcher<Appointment>(`/patients/${patientId}/appointments`, {
    method: 'POST',
    body: { appointment },
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  })
}

export const reschedulePatientAppointment = (
  patientId: string,
  appointmentId: string,
  appointment,
  token: string
) => {
  return fetcher<Appointment>(`/patients/${patientId}/appointments/${appointmentId}/reschedule`, {
    method: 'PATCH',
    body: { appointment },
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  })
}

export const login = (loginObject) => {
  return baseJsonFetcherWithStatus<LoginAuth>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(loginObject),
    clientSlugPath: ''
  });
};

// Turns the hint token from a login link into the masked contact point the link
// was delivered to, so the password screen can name the account it is about.
// The tenant comes from the subdomain, so there is no client slug in the path.
export const resolveLoginHint = (token: string) => {
  return baseJsonFetcherWithStatus<LoginHintResolution>('/auth/resolve-login-hint', {
    method: 'POST',
    body: JSON.stringify({ token }),
    clientSlugPath: ''
  });
};

export const logout = () => {
  return baseJsonFetcher<LoginAuth>('/auth/logout', {
    method: 'POST',
    clientSlugPath: ''
  });
};

export const resetPassword = (emailObject) => {
  return baseJsonFetcher<RodAuth>('/auth/reset-password-request', {
    method: 'POST',
    body: JSON.stringify(emailObject),
    clientSlugPath: ''
  });
};

export const updatePassword = (passwordObject) => {
  return baseJsonFetcher<RodAuth>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify(passwordObject),
    clientSlugPath: ''
  });
};

export const verifyAccount = (params) => {
  return baseJsonFetcher<RodAuth>(`/auth/verify-account`, {
    method: 'POST',
    body: JSON.stringify(params),
    clientSlugPath: ''
  });
};

export const refreshAuth = (params) => {
  return baseJsonFetcher<RefreshAuth>('/auth/jwt-refresh', {
    method: 'POST',
    body: JSON.stringify(params),
    clientSlugPath: ''
  });
};

export const multifactorAuth = () => {
  return baseJsonFetcher<MfaAuth>('/auth/multifactor-auth', {
    method: 'POST',
    clientSlugPath: ''
  });
};

export const otpAuth = (params) => {
  return baseJsonFetcher<RodAuth>('/auth/otp-auth', {
    method: 'POST',
    body: JSON.stringify(params),
    clientSlugPath: ''
  });
};

export const webauthnAuth = (webauthn = {}) => {
  return baseJsonFetcher<WebauthnAuth>('/auth/webauthn-auth', {
    method: 'POST',
    body: JSON.stringify(webauthn),
    clientSlugPath: ''
  });
};

export const setupOtp = (params = {}) => {
  return baseJsonFetcher<OtpConfig>('/auth/otp-setup', {
    method: 'POST',
    body: JSON.stringify(params),
    clientSlugPath: ''
  });
};

export const disableOtp = (params): Promise<OtpConfig> => {
  return baseJsonFetcher<OtpConfig>('/auth/otp-disable', {
    method: 'POST',
    body: JSON.stringify(params),
    clientSlugPath: ''
  });
};

export const setupWebauthn = (params = {}) => {
  return baseJsonFetcher<WebAuthnConfig>('/auth/webauthn-setup', {
    method: 'POST',
    body: JSON.stringify(params),
    clientSlugPath: ''
  });
};

export const removeWebauthn = (params = {}) => {
  return baseJsonFetcher<WebauthnRemove>('/auth/webauthn-remove', {
    method: 'POST',
    body: JSON.stringify(params),
    clientSlugPath: ''
  });
};

export const getMe = () => {
  return fetcher<Patient>('/patients/me')
}

export const reset = (details, token) => {
  return fetcher<Patient>('/patients/reset',
    {
      method: 'POST',
      body: { patient: details },
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    }
  )
}

export const refreshToken = (token) => {
  return fetcher<Patient>('/patients/refresh_login_token',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    }
  )
}

// ################ MESSAGES ################ //
export const createMessages = (message) => {
  return fetcher<Message>(`/messages`, {
    method: 'POST',
    body: { message },
  });
};

// ################ Verification Codes ################ //
export const requestCode = (rawEndpoint, platform, languagePreference='en') => {
  const endpoint = platform == 'sms' ? `+1${rawEndpoint.replace(/\D/g,'')}` : rawEndpoint
  return fetcher(`/verification_codes`, {
    method: 'POST',
    body: { verificationCode: {endpoint, platform, languagePreference } },
  });
}

export const verifyCode = (rawEndpoint, platform, code) => {
  const endpoint = platform == 'sms' ? `+1${rawEndpoint.replace(/\D/g,'')}` : rawEndpoint
  return fetcher(`/verification_codes/verify`, {
    method: 'POST',
    body: { verificationCode: {endpoint, code} },
  });
}

export const getBrand = () => {
  return fetcher<Brand>(`/client/brand`);
}

// Session heartbeat - extends session when user is active
export const heartbeat = () => {
  return fetcher<{ sessionExp: string }>('/patient_sessions/heartbeat', {
    method: 'POST',
  });
}

// ################ SIGNATURE DOCUMENTS ################ //
export const getSignatureDocument = (id: string, headers = {}) => {
  return fetcher<SignatureDocument>(`/signature_documents/${id}`, {
    headers
  });
}

export const updateSignatureDocument = (id: string, signatureDocument: Partial<SignatureDocument>, headers = {}) => {
  return fetcher<SignatureDocument>(`/signature_documents/${id}`, {
    method: 'PATCH',
    body: { signature_document: signatureDocument },
    headers
  });
}

export const getSignatureDocumentSignatures = (signatureDocumentId: string, headers = {}) => {
  return fetcher<Signature[]>(`/signature_documents/${signatureDocumentId}/signatures`, {
    headers
  });
}

// ################ SIGNATURES ################ //
export const getSignature = (signatureDocumentId: string, signatureId: string, headers = {}) => {
  return fetcher<Signature>(`/signature_documents/${signatureDocumentId}/signatures/${signatureId}`, {
    headers
  });
}

export const updateSignature = (signatureDocumentId: string, signatureId: string, signature: Partial<Signature>, headers = {}) => {
  return fetcher<Signature>(`/signature_documents/${signatureDocumentId}/signatures/${signatureId}`, {
    method: 'PATCH',
    body: { signature },
    headers
  });
}

export const completeSignature = (signatureDocumentId: string, signatureId: string, signatureData: {
  status: 'signed';
  signerName: string;
  signerPath: string;
  signedAt: string;
  signature?: string;
  cryptoKeyId?: string;
  position?: { page: number; x: number; y: number };
}, headers = {}) => {
  return fetcher<Signature>(`/signature_documents/${signatureDocumentId}/signatures/${signatureId}`, {
    method: 'PATCH',
    body: {
      signature: signatureData
    },
    headers
  });
}